/** Suppressions, API keys, webhooks, knowledge-base, AI, analytics.
 * Grouped here so we don't spawn a file per five-endpoint resource — pull
 * them apart when any one grows past ~50 lines. */

import { apiClient, type PageEnvelope } from '@/lib/api-client';

// --- Suppressions ----------------------------------------------------
export interface Suppression {
  id: string;
  email: string;
  reason: string;
  notes: string | null;
}

export const suppressionsApi = {
  list: () =>
    apiClient
      .get<PageEnvelope<Suppression>>('/api/v1/suppressions')
      .then((r) => r.items),
  add: (input: { email: string; reason: string; notes?: string }) =>
    apiClient.post<Suppression>('/api/v1/suppressions', input),
  remove: (id: string) => apiClient.del<void>(`/api/v1/suppressions/${id}`),
};

// --- API keys --------------------------------------------------------
export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  created_by_operator_id: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}
export interface ApiKeyCreated extends ApiKey {
  key: string;
}

export const apiKeysApi = {
  list: () =>
    apiClient.get<PageEnvelope<ApiKey>>('/api/v1/api-keys').then((r) => r.items),
  create: (input: { name: string }) =>
    apiClient.post<ApiKeyCreated>('/api/v1/api-keys', input),
  revoke: (id: string) => apiClient.del<void>(`/api/v1/api-keys/${id}`),
};

// --- Webhooks --------------------------------------------------------
export interface WebhookEndpoint {
  id: string;
  url: string;
  event_types: string[];
  status: string;
  description: string | null;
  secret?: string;
  created_at: string;
  last_delivered_at: string | null;
  last_error_at: string | null;
  last_error_message: string | null;
}

export const webhooksApi = {
  overview: () =>
    apiClient.get<{ items: WebhookEndpoint[]; event_types: string[] }>('/api/v1/webhooks'),
  create: (input: { url: string; event_types: string[]; description?: string }) =>
    apiClient.post<WebhookEndpoint>('/api/v1/webhooks', input),
  update: (id: string, input: Partial<WebhookEndpoint>) =>
    apiClient.patch<WebhookEndpoint>(`/api/v1/webhooks/${id}`, input),
  remove: (id: string) => apiClient.del<void>(`/api/v1/webhooks/${id}`),
};

// --- Knowledge base --------------------------------------------------
export interface KnowledgeDoc {
  id: string;
  name: string;
  kind: string;
  source_size_bytes: number;
  text_length: number;
  text_preview: string;
}

export const knowledgeBaseApi = {
  list: () =>
    apiClient
      .get<PageEnvelope<KnowledgeDoc>>('/api/v1/knowledge-base')
      .then((r) => r.items),
  upload: (input: { name: string; kind: string; data_base64: string }) =>
    apiClient.post<KnowledgeDoc>('/api/v1/knowledge-base', input),
  importUrl: (input: { url: string; name?: string }) =>
    apiClient.post<KnowledgeDoc>('/api/v1/knowledge-base/import-url', input),
  remove: (id: string) => apiClient.del<void>(`/api/v1/knowledge-base/${id}`),
};

// AI is a built-in feature — no REST surface. Openers render inline during
// send, replies auto-classify + auto-draft in the IMAP watcher, and sequence
// seeding is a flag on POST /campaigns. See ARCHITECTURE.md §9.

// --- Analytics -------------------------------------------------------
export interface KpiSummary {
  total_leads: number;
  sent: number;
  replied: number;
  bounced: number;
  opened: number;
  clicked: number;
  ai_cost_micro_cents_last_30d: number;
}

export interface ThreadBubble {
  kind: 'sent' | 'reply' | 'manual_reply' | 'manual_forward';
  direction: 'inbound' | 'outbound';
  at: string | null;
  event_id?: string;
  from: string | null;
  from_name: string | null;
  to: string | null;
  subject: string | null;
  body: string | null;
  body_html: string | null;
  status?: string;
  intent?: string | null;
  engine?: 'deepseek' | 'mlm' | null;
  raw_intent?: string | null;
  provenance?: string[];
  message_id_header?: string | null;
}

export interface ReplyThread {
  event_id: string;
  subject: string | null;
  lead: {
    id: string;
    email: string;
    name: string | null;
    company: string | null;
    status: string;
  };
  compose: {
    mailbox_id: string | null;
    mailbox_email: string | null;
    to: string;
    subject_hint: string | null;
    can_send: boolean;
  };
  messages: ThreadBubble[];
}

export const analyticsApi = {
  summary: () => apiClient.get<KpiSummary>('/api/v1/analytics/summary'),
  insights: (days = 30) =>
    apiClient.get<{ series: Array<{ day: string; sent: number; replied: number }> }>(
      '/api/v1/insights',
      { days },
    ),
  replies: () =>
    apiClient.get<{ items: Array<Record<string, unknown>> }>('/api/v1/replies'),
  replyThread: (eventId: string) =>
    apiClient.get<ReplyThread>(`/api/v1/replies/${eventId}`),
  sendReply: (
    eventId: string,
    body: { body_html: string; subject?: string; attachment_ids?: string[] },
  ) =>
    apiClient.post<{ ok: boolean; event_id: string; message_id: string }>(
      `/api/v1/replies/${eventId}/reply`,
      body,
    ),
  sendForward: (
    eventId: string,
    body: { to: string; body_html: string; subject?: string; attachment_ids?: string[] },
  ) =>
    apiClient.post<{ ok: boolean; event_id: string; message_id: string }>(
      `/api/v1/replies/${eventId}/forward`,
      body,
    ),
};
